
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

type CloudflareImageVariant = 'public' | 'md' | 'sm';

const cloudflareImageWidths: Record<CloudflareImageVariant, number> = {
    public: 1080,
    md: 640,
    sm: 128,
};

export function toCloudflareImage(url: string, variant: CloudflareImageVariant = 'public'): string {
    if (!url) return 'https://placehold.co/600x800.png'; // Return a placeholder if URL is empty

    // Handle both Firebase Storage URLs and direct Cloudflare URLs
    if (url.startsWith('https://images.artvau.lt/')) {
        try {
            const cloudflareUrl = new URL(url);
            cloudflareUrl.searchParams.set('w', String(cloudflareImageWidths[variant]));
            cloudflareUrl.searchParams.set('q', '82');
            cloudflareUrl.searchParams.set('fit', 'contain');
            return cloudflareUrl.toString();
        } catch {
            return url;
        }
    }

    if (!url.startsWith('https://firebasestorage.googleapis.com')) {
        return url; // Not a Firebase URL, return as is
    }

    try {
        // Find the start of the path part after '/o/'
        const pathStartIndex = url.indexOf('/o/');
        if (pathStartIndex === -1) return url;

        // Extract the path and query parameters
        const pathAndQuery = url.substring(pathStartIndex + 3);
        
        // Split path from query parameters
        const [encodedPath, queryParams = ''] = pathAndQuery.split('?');
        
        if (!encodedPath) return url;
        
        // Decode the path
        const decodedPath = decodeURIComponent(encodedPath);

        const cloudflareUrl = new URL(`https://images.artvau.lt/${decodedPath}`);
        const sourceParams = new URLSearchParams(queryParams);
        const token = sourceParams.get('token');
        if (token) {
            cloudflareUrl.searchParams.set('token', token);
        }

        cloudflareUrl.searchParams.set('w', String(cloudflareImageWidths[variant]));
        cloudflareUrl.searchParams.set('q', '82');
        cloudflareUrl.searchParams.set('fit', 'contain');

        return cloudflareUrl.toString();

    } catch (e) {
        console.error("Failed to parse image URL", e);
        return url;
    }
}

export function toWordpressCloudflareImage(url: string): string {
    if (!url || !url.startsWith('https://artvault.westondeboer.com/wp-content/uploads/')) {
        return url;
    }
    try {
        const path = url.replace('https://artvault.westondeboer.com/wp-content/uploads/', '');
        const cloudflareUrl = new URL(`https://images.artvau.lt/wp/${path}`);
        cloudflareUrl.searchParams.set('w', '1080');
        cloudflareUrl.searchParams.set('q', '82');
        cloudflareUrl.searchParams.set('fit', 'contain');
        return cloudflareUrl.toString();
    } catch (e) {
        console.error("Failed to parse WordPress image URL", e);
        return url;
    }
}

export function shouldBypassNextImageOptimization(url: string): boolean {
    if (!url) return false;

    return (
        url.startsWith('https://images.artvau.lt/') ||
        url.startsWith('https://firebasestorage.googleapis.com/') ||
        /\.gif($|\?)/i.test(url)
    );
}

type ImageAssetInput = {
    imageUrl?: string;
    imageUrls?: string[];
    imageAssets?: {
        original?: string;
        card?: string;
        thumb?: string;
    };
};

export function getArtworkImageUrl(item: ImageAssetInput, variant: CloudflareImageVariant = 'md'): string {
    if (variant === 'sm' && item.imageAssets?.thumb) return item.imageAssets.thumb;
    if (variant === 'md' && item.imageAssets?.card) return item.imageAssets.card;
    if (variant === 'public' && item.imageAssets?.original) return item.imageAssets.original;

    const sourceUrl = item.imageUrls?.[0] || item.imageUrl || item.imageAssets?.original || 'https://placehold.co/600x800.png';
    return toCloudflareImage(sourceUrl, variant);
}

export function getImageUrlVariant(url: string, variant: CloudflareImageVariant = 'md'): string {
    return toCloudflareImage(url || 'https://placehold.co/600x800.png', variant);
}

export function buildImageAssetsFromUrl(url: string) {
    return {
        original: url,
        card: getImageUrlVariant(url, 'md'),
        thumb: getImageUrlVariant(url, 'sm'),
    };
}
